import { Device } from '@twilio/voice-sdk';

import { reportAxiosError, reportTwilioError } from '~/Utils';

/**
 * How long before a token expires the SDK emits `tokenWillExpire`. The SDK default of 10s leaves
 * no room for a slow request or a retry; two minutes against the token's one-hour life is ample.
 */
export const TOKEN_REFRESH_MS = 120 * 1000;

/**
 * Keeps a registered device usable past its access token's expiry. Without this the device
 * unregisters when the token dies and the browser silently stops receiving calls.
 */
export const attachTokenRefreshHandler = (
  device: Device,
  fetchAccessToken: () => Promise<string | undefined>
): void => {
  device.on('tokenWillExpire', async (expiringDevice: Device) => {
    let token: string | undefined;
    try {
      token = await fetchAccessToken();
    } catch (error) {
      // The two per-call containers let a failed token request reject; the call center's fetch
      // swallows its own failure and resolves undefined. A background refresh failure is not
      // actionable by the adjuster, so report it without the generic dialog.
      await reportAxiosError(error, false);
      return;
    }

    // The device can be destroyed while the token request is in flight: an in-call container
    // destroys its device when the call ends, the call center its own on teardown.
    if (!token || expiringDevice.state === Device.State.Destroyed) {
      return;
    }

    try {
      // updateToken throws InvalidArgumentError on anything that is not a string, and
      // InvalidStateError on a destroyed device - neither is an axios error.
      expiringDevice.updateToken(token);
    } catch (error) {
      reportTwilioError(error);
    }
  });
};
